// src/hooks/useImageCompression.js
import { useState, useCallback } from "react";
import {
  compressImage,
  validateAndCompressImage,
  formatFileSize,
  getBase64Size,
  isMobileDevice,
} from "../utils/imageCompression";

/**
 * Custom hook for image compression
 * Supports both simple and advanced compression workflows
 * @returns {Object} Compression state and handlers
 */
const useImageCompression = () => {
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressing, setCompressing] = useState(false); // Alias for backward compatibility
  const [compressionProgress, setCompressionProgress] = useState(null);
  const [progress, setProgress] = useState(null); // Alias for backward compatibility
  const [error, setError] = useState(null);

  /**
   * Compress a single image file (simple version)
   * @param {File} file - Image file to compress
   * @param {number} maxSizeKB - Target size in KB (default: 700)
   * @returns {Promise<Object>} Compression result with stats
   */
  const compressSingleImage = useCallback(async (file, maxSizeKB = 700) => {
    try {
      setCompressing(true);
      setIsCompressing(true);

      const originalSize = file.size;
      const originalSizeFormatted = formatFileSize(originalSize);

      const mobile = isMobileDevice();
      const compressed = await compressImage(file, maxSizeKB, mobile);

      const compressedSize = getBase64Size(compressed);
      const compressedSizeFormatted = formatFileSize(compressedSize);

      const reduction = Math.round(
        ((originalSize - compressedSize) / originalSize) * 100,
      );

      console.log(
        `🖼️ "${file.name}": ${originalSizeFormatted} → ${compressedSizeFormatted} (${reduction}% reduction)`,
      );

      return {
        compressed,
        originalSize,
        compressedSize,
        reduction,
      };
    } catch (err) {
      console.error("Error compressing image:", err);
      throw err;
    } finally {
      setCompressing(false);
      setIsCompressing(false);
    }
  }, []);

  /**
   * Compress a single image file (advanced version with validation)
   * @param {File} file - Image file to compress
   * @param {number} maxSizeKB - Target size in KB (default: 700)
   * @returns {Promise<string>} Base64 compressed image
   */
  const compress = useCallback(async (file, maxSizeKB = 700) => {
    if (!file) {
      setError("No file provided");
      return null;
    }

    setIsCompressing(true);
    setCompressing(true);
    setError(null);
    setCompressionProgress({
      fileName: file.name,
      originalSize: formatFileSize(file.size),
      status: "compressing",
    });

    try {
      const result = await validateAndCompressImage(file, maxSizeKB);

      setCompressionProgress({
        fileName: file.name,
        originalSize: formatFileSize(result.originalSize),
        compressedSize: formatFileSize(result.compressedSize),
        compressionRatio: result.compressionRatio,
        wasCompressed: result.wasCompressed,
        status: "complete",
      });

      return result.compressed;
    } catch (err) {
      console.error("Compression error:", err);
      setError(err.message || "Failed to compress image");
      setCompressionProgress({
        fileName: file.name,
        status: "error",
        error: err.message,
      });
      return null;
    } finally {
      setIsCompressing(false);
      setCompressing(false);
    }
  }, []);

  /**
   * Compress multiple images sequentially
   * @param {FileList|File[]} files - Array of image files
   * @param {number} maxSizeKB - Target size in KB (default: 700)
   * @returns {Promise<string[]>} Array of base64 compressed images
   */
  const compressMultipleImages = useCallback(
    async (files, maxSizeKB = 700) => {
      try {
        setCompressing(true);
        setIsCompressing(true);

        const mobile = isMobileDevice();
        const results = [];
        const fileArray = Array.from(files);

        for (let i = 0; i < fileArray.length; i++) {
          const file = fileArray[i];

          // Update progress
          setProgress({
            current: i + 1,
            total: fileArray.length,
            fileName: file.name,
          });

          if (mobile) {
            // Sequential processing for mobile
            const result = await compressSingleImage(file, maxSizeKB);
            results.push(result.compressed);
          } else {
            // Can use parallel for desktop
            const compressed = await compressImage(file, maxSizeKB, mobile);
            results.push(compressed);
          }
        }

        setProgress(null);
        return results;
      } catch (err) {
        console.error("Error compressing multiple images:", err);
        setProgress(null);
        throw err;
      } finally {
        setCompressing(false);
        setIsCompressing(false);
      }
    },
    [compressSingleImage],
  );

  /**
   * Compress multiple images (advanced version with validation)
   * @param {FileList|File[]} files - Array of image files
   * @param {number} maxSizeKB - Target size in KB (default: 700)
   * @returns {Promise<string[]>} Array of base64 compressed images
   */
  const compressMultiple = useCallback(async (files, maxSizeKB = 700) => {
    const fileArray = Array.from(files);
    const results = [];

    setIsCompressing(true);
    setCompressing(true);
    setError(null);

    for (let i = 0; i < fileArray.length; i++) {
      const file = fileArray[i];

      setCompressionProgress({
        fileName: file.name,
        currentFile: i + 1,
        totalFiles: fileArray.length,
        status: "compressing",
      });

      try {
        const result = await validateAndCompressImage(file, maxSizeKB);
        results.push(result.compressed);
      } catch (err) {
        console.error(`Failed to compress ${file.name}:`, err);
        setError(`Failed to compress ${file.name}`);
        results.push(null);
      }
    }

    setIsCompressing(false);
    setCompressing(false);
    setCompressionProgress({
      status: "complete",
      totalFiles: fileArray.length,
      successCount: results.filter((r) => r !== null).length,
    });

    return results;
  }, []);

  /**
   * Reset compression state
   */
  const reset = useCallback(() => {
    setIsCompressing(false);
    setCompressing(false);
    setCompressionProgress(null);
    setProgress(null);
    setError(null);
  }, []);

  return {
    // Simple API (original version)
    compressing,
    progress,
    compressSingleImage,
    compressMultipleImages,

    // Advanced API (validation version)
    compress,
    compressMultiple,
    isCompressing,
    compressionProgress,
    error,
    reset,
  };
};

export default useImageCompression;
